import AddIcon from "@mui/icons-material/Add";
import { Box, Button, CircularProgress, InputAdornment, List, ListItem, Stack, TextField, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useModals } from "../../../context/ModalContext";
import { useWatchInvoiceNotifications } from "../../../notifications/hooks/useWatchInvoiceNotifications";
import { useAppSelector } from "../../../redux/hooks/redux";
import { BaseModalComponent } from "../../common/BaseModalComponent";
import { PaymentMethodSelector, usePaymentMethodState } from "../../invoice/create/PaymentMethods";
import { useCompanyPaymentsStatusQuery } from "../../invoice/create/hooks/usePayments";
import type { IInvoice } from "../../invoice/create/interfaces/invoice.interfaces";
import { InvoiceDetailsView } from "../../invoice/view/components/DetailsView";
import { InvoiceListItem } from "../../invoice/view/components/shared/InvoiceListItem";
import { canIssueNewInvoice } from "../../invoice/view/components/shared/invoiceLifecycle";
import { useDialog } from "../../ui/dialogs/useDialog";
import { useDepositInvoicesByQuote, useReissueDepositInvoice } from "../hooks/useDepositInvoices";
import { getDepositTriggerButtonSx, issueDepositInvoiceButtonSx } from "./depositActionButtonStyles";

interface DepositInvoiceListModalContentProps {
  quoteId: number;
  /** Current deposit amount configured on the quote; enables the first-time creation flow when null. */
  initialDepositAmount?: number | null;
  /** Upper bound for the deposit amount (the quote total). Validated client-side before submit. */
  quoteTotal?: number | null;
  /** Optional scope-of-work uid forwarded to the details preview. */
  scopeOfWorkUid?: string;
  /** Modal stack key for the deposit invoice preview (so it stacks above this list). */
  invoicePreviewModalKey: string;
}

const EmptyStateMessage = ({ children, error, sx }: { children: ReactNode; error?: boolean; sx?: SxProps<Theme> }) => (
  <Typography variant="body2" color={error ? "error" : "text.secondary"} sx={{ mb: 2, maxWidth: 520, lineHeight: 1.6, ...sx }}>
    {children}
  </Typography>
);

/**
 * Mirrors `InvoiceListModalContent` but scoped to a Quote's deposit invoices. Lets the admin
 * audit history (active/refunded/cancelled) and reissue a fresh deposit invoice when the
 * previous one was refunded or cancelled.
 */
export function DepositInvoiceListModalContent({
  quoteId,
  initialDepositAmount,
  quoteTotal,
  scopeOfWorkUid,
  invoicePreviewModalKey,
}: DepositInvoiceListModalContentProps) {
  const queryClient = useQueryClient();
  const { data: invoices = [], isLoading, isError, refetch } = useDepositInvoicesByQuote(quoteId);
  const { reissue, isPending: isReissuing } = useReissueDepositInvoice(quoteId);
  const { showDialog, DialogComponent } = useDialog();
  const { openModal } = useModals();

  // When the quote has no depositAmount yet we switch to a first-time creation flow: the admin
  // types the amount right here and we POST it as the seed value for the new invoice + quote.
  const needsAmountInput = initialDepositAmount == null;
  const [amountInput, setAmountInput] = useState<string>("");
  const parsedAmount = Number(amountInput);
  const companyId = useAppSelector((state) => state.userState.activeCompanyId);
  const { data: paymentAvailableData } = useCompanyPaymentsStatusQuery(companyId);
  const paymentMethod = usePaymentMethodState();
  const isPositive = Number.isFinite(parsedAmount) && parsedAmount > 0;
  const exceedsTotal = quoteTotal != null && parsedAmount > quoteTotal;
  const isAmountValid = isPositive && !exceedsTotal;
  const amountHelperText = exceedsTotal
    ? `Must be ≤ quote total ($${quoteTotal!.toFixed(2)})`
    : quoteTotal != null
      ? `Quote total: $${quoteTotal.toFixed(2)}`
      : undefined;

  // Refetch the list when a deposit invoice transitions to paid via webhook/sync.
  useWatchInvoiceNotifications({
    onNotificationArrived: () => {
      queryClient.invalidateQueries({ queryKey: ["quotes", quoteId, "deposit-invoices"] });
    },
  });

  const canIssue = canIssueNewInvoice(invoices);

  const handleSelectInvoice = (invoice: IInvoice) => {
    openModal(
      { key: invoicePreviewModalKey, preventParentTrigger: true },
      <Box sx={{ minHeight: "700px" }}>
        <InvoiceDetailsView invoiceUid={invoice.uid} scopeOfWorkUid={scopeOfWorkUid} />
      </Box>,
      null,
    );
  };

  const handleIssueDepositInvoice = () => {
    if (needsAmountInput && !isAmountValid) return;

    const payload = needsAmountInput ? { depositAmount: parsedAmount, paymentMethods: paymentMethod?.selectedPayments ?? [] } : undefined;
    const message = needsAmountInput
      ? `A deposit invoice for $${parsedAmount.toFixed(2)} will be created and sent to the client. Continue?`
      : "A new deposit invoice will be created and sent to the client. Any payment links from the previous deposit will remain invalidated. Continue?";

    showDialog({
      type: "warning",
      title: needsAmountInput ? "Create Deposit Invoice" : "Issue New Deposit Invoice",
      message,
      confirmLabel: needsAmountInput ? "Create Invoice" : "Issue Invoice",
      cancelLabel: "Cancel",
      onConfirm: () => reissue(payload),
      onCancel: () => undefined,
    });
  };

  const submitLabel = isReissuing ? "Issuing..." : invoices.length ? "Issue New Deposit Invoice" : "Create Deposit Invoice";

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ py: 1 }}>
        <ListShell>
          <Stack spacing={2} alignItems="flex-start">
            <EmptyStateMessage error sx={{ textAlign: "left", mb: 0 }}>
              Could not load deposit invoices for this quote.
            </EmptyStateMessage>
            <Button variant="text" size="small" onClick={() => void refetch()} sx={getDepositTriggerButtonSx("muted")}>
              Retry
            </Button>
          </Stack>
        </ListShell>
        {DialogComponent}
      </Box>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      {/* Stacked preview modal renderer; openModal will only mount content if this exists. */}
      <BaseModalComponent
        modalKey={invoicePreviewModalKey}
        modalLabel="Deposit Invoice"
        defaultSizes={{ width: "80%", height: "85%" }}
        preventParentTrigger
      />

      {!invoices.length && (
        <Box>
          <PaymentMethodSelector availablePayments={paymentAvailableData} canMarkAsPaid={false} />
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: needsAmountInput ? "flex-start" : "space-between",
          gap: 1.5,
          px: 2,
          mb: 1,
          mt: 3,
          minHeight: 36,
          flexWrap: "wrap",
        }}
      >
        {canIssue && needsAmountInput && (
          <TextField
            size="small"
            label="Deposit amount"
            type="number"
            autoFocus
            value={amountInput}
            onChange={(e) => setAmountInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && isAmountValid && !isReissuing) handleIssueDepositInvoice();
            }}
            error={amountInput !== "" && !isAmountValid}
            helperText={amountInput !== "" || quoteTotal != null ? amountHelperText : undefined}
            inputProps={{ min: 0, max: quoteTotal ?? undefined, step: "0.01" }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            sx={{ width: "40%", maxWidth: 340 }}
          />
        )}
        {canIssue && (
          <Button
            variant="text"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />}
            onClick={handleIssueDepositInvoice}
            disabled={isReissuing || (needsAmountInput && !isAmountValid) || !paymentMethod?.selectedPayments.length}
            sx={{ ...issueDepositInvoiceButtonSx, mt: -3 }}
          >
            {submitLabel}
          </Button>
        )}
      </Box>

      {invoices.length === 0 ? (
        <ListShell>
          <EmptyStateMessage sx={{ textAlign: "left", mb: 0 }}>No deposit invoices yet for this quote.</EmptyStateMessage>
        </ListShell>
      ) : (
        <List disablePadding>
          {invoices.map((inv) => (
            <InvoiceListItem key={inv.uid} invoice={inv} onClick={() => handleSelectInvoice(inv)} />
          ))}
        </List>
      )}
      {DialogComponent}
    </Box>
  );
}

const ListShell = ({ children }: { children: ReactNode }) => (
  <List disablePadding>
    <ListItem
      sx={{
        flexDirection: "column",
        alignItems: "stretch",
        py: 2.5,
        px: 2,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      {children}
    </ListItem>
  </List>
);
